import { motion, AnimatePresence } from "framer-motion";
import { X, Trash2, ArrowRight, ShoppingBag, Clock3 } from "lucide-react";
import { ALL_ITEMS } from "@/data/catalog";

export default function CartDrawer({ open, items, onClose, onRemove, onClear, onProceed }) {
  const cartItems = ALL_ITEMS.filter((i) => items.includes(i.id));
  const total = cartItems.reduce((s, i) => s + i.price, 0);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[95] bg-[#0B192C]/60 backdrop-blur-sm" onClick={onClose} />
          <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 bottom-0 z-[96] w-full max-w-md bg-white shadow-2xl flex flex-col" data-testid="cart-drawer">
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#D92638]/10 flex items-center justify-center">
                  <ShoppingBag className="w-5 h-5 text-[#D92638]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0B192C]">Your cart</h3>
                  <p className="text-xs text-slate-500">{cartItems.length} item{cartItems.length !== 1 ? "s" : ""}</p>
                </div>
              </div>
              <button onClick={onClose} aria-label="Close cart" data-testid="cart-close-button"
                className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              {cartItems.length === 0 && (
                <div className="text-center py-16 text-slate-400 text-sm" data-testid="cart-empty">Your cart is empty. Add tests from the catalog.</div>
              )}
              {cartItems.map((item) => (
                <div key={item.id} className="flex items-start gap-3.5 bg-[#F8FAFC] border border-slate-200/80 rounded-2xl p-4" data-testid={`cart-item-${item.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#0B192C] leading-snug">{item.name}</div>
                    <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-500">
                      <Clock3 className="w-3 h-3 text-[#D92638]" /> {item.tat}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono-med font-bold text-[#0B192C]">₹{item.price.toLocaleString("en-IN")}</div>
                    <button onClick={() => onRemove(item.id)} data-testid={`cart-remove-${item.id}`}
                      className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-red-500 hover:text-red-600 transition-colors">
                      <Trash2 className="w-3 h-3" /> Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {cartItems.length > 0 && (
              <div className="border-t border-slate-100 px-6 py-5 bg-white">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-slate-500">Total payable</span>
                  <span className="font-mono-med text-2xl font-bold text-[#0B192C]" data-testid="cart-total">₹{total.toLocaleString("en-IN")}</span>
                </div>
                <button onClick={onProceed} data-testid="cart-proceed-button"
                  className="w-full flex items-center justify-center gap-2 bg-[#D92638] hover:bg-[#B81D2D] text-white font-semibold py-4 rounded-full transition-all duration-200 active:scale-[0.98] shadow-[0_10px_30px_-8px_rgba(217,38,56,0.5)]">
                  Proceed to booking & payment <ArrowRight className="w-4 h-4" />
                </button>
                <p className="mt-3 text-center text-[11px] text-slate-400">Pay securely by UPI at checkout · free home collection</p>
                <button onClick={onClear} data-testid="cart-clear-all"
                  className="mt-2 w-full text-center text-xs text-slate-400 hover:text-red-500 transition-colors">Clear cart</button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
